import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import { Dialog, Typography, Stack, Link } from '@mui/material'
import CoreHeader from './CoreHeader'
import CoreFooter from './CoreFooter'
import CoreTitle from './CoreTitle'
import Scrollbar from '../../control/Scrollbar'
import { getUrl, formatTelephone, formatTelephoneNumberOnly } from '../../util/utilityFunction'
import useContact from '../../hooks/map/useContact'
import { ReactComponent as AddressIcon } from '../../icon/contact_address.svg'
import { ReactComponent as TelephoneIcon } from '../../icon/contact_telephone.svg'
import { ReactComponent as CallCenterIcon } from '../../icon/contact_call_center.svg'
import { ReactComponent as WebsiteIcon } from '../../icon/contact_website.svg'
import { ReactComponent as EmailIcon } from '../../icon/contact_email.svg'
import { ReactComponent as LineIcon } from '../../icon/contact_line.svg'

const NoImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_no_image.png`
const AnnounceImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_announce.png`

type InfoContactProps = {}

const InfoContact = (props: InfoContactProps) => {
    const { data, noDataMessage, isDataNotFound } = useContact()
    const [openDialog, setOpenDialog] = useState(true)
    const { t } = useTranslation()

    return (
        <Dialog
            fullScreen
            open={openDialog}
            onClose={() => {
                setOpenDialog(false)
            }}
        >
            <CoreHeader />

            <StyledContent>
                <div style={{ width: '100%', height: '100%' }}>
                    <Scrollbar scrollbarTrackBorderRadius={10} scrollbarThumbBorderRadius={10}>
                        <StyledContainer>
                            <CoreTitle
                                label={t('MENU.CONTACT')}
                                date={data?.versionDateTime}
                                displayDate={isDataNotFound ? false : data?.versionDateTime ? true : false}
                            />
                            <StyledContent>
                                {isDataNotFound ? (
                                    <StyledDivNotFound>
                                        <Stack alignItems="center">
                                            <StyledImgAnnounce src={AnnounceImage} />
                                            <Typography>{noDataMessage}</Typography>
                                        </Stack>
                                    </StyledDivNotFound>
                                ) : (
                                    <>
                                        {data?.coverImageUrl ? (
                                            <StyledDivCover>
                                                <StyledImgCover src={data?.coverImageUrl} />
                                            </StyledDivCover>
                                        ) : (
                                            <StyledDivCoverNoImage>
                                                <Stack alignItems="center">
                                                    <StyledImgNoImage src={NoImage} />
                                                    <Typography>{t('LABEL.NO_IMAGE')}</Typography>
                                                </Stack>
                                            </StyledDivCoverNoImage>
                                        )}
                                        {noDataMessage ? (
                                            <StyledDivNotFound>
                                                <Stack alignItems="center">
                                                    <Typography>{noDataMessage}</Typography>
                                                </Stack>
                                            </StyledDivNotFound>
                                        ) : (
                                            <StyledDivDetail>
                                                <StyledTypoNBTC>{`${data?.organizationName}`}</StyledTypoNBTC>
                                                <StyledDivInfo>
                                                    {/* ที่อยู่ */}
                                                    {data?.address && (
                                                        <StyledDivRow>
                                                            <StyledDivIcon>
                                                                <IconAddress />
                                                            </StyledDivIcon>
                                                            <StyledDivText>
                                                                <StyledTypoInfo>
                                                                    {`${t('LABEL.BACKOFFICE.CONTACT.ADDRESS')} : ${
                                                                        data?.address
                                                                    }`}
                                                                </StyledTypoInfo>
                                                            </StyledDivText>
                                                        </StyledDivRow>
                                                    )}
                                                    {/* เบอร์ติดต่อ */}
                                                    {data?.contactNumber && (
                                                        <StyledDivRow>
                                                            <StyledDivIcon>
                                                                <IconTelephone />
                                                            </StyledDivIcon>
                                                            <StyledDivText>
                                                                <StyledTypoInfo>
                                                                    {`${t('LABEL.BACKOFFICE.CONTACT.TELEPHONE')} : `}
                                                                    <StyledLink
                                                                        href={`tel:${formatTelephoneNumberOnly(
                                                                            data?.contactNumber,
                                                                        )}`}
                                                                    >
                                                                        {formatTelephone(
                                                                            formatTelephoneNumberOnly(
                                                                                data?.contactNumber,
                                                                            ),
                                                                        )}
                                                                    </StyledLink>
                                                                </StyledTypoInfo>
                                                            </StyledDivText>
                                                        </StyledDivRow>
                                                    )}
                                                    {/* Call Center */}
                                                    {data?.callCenter && (
                                                        <StyledDivRow>
                                                            <StyledDivIcon>
                                                                <IconCallCenter />
                                                            </StyledDivIcon>
                                                            <StyledDivText>
                                                                <StyledTypoInfo>
                                                                    {`${t('LABEL.BACKOFFICE.CONTACT.CALL_CENTER')} : `}
                                                                    <StyledLink
                                                                        href={`tel:${formatTelephoneNumberOnly(
                                                                            data?.callCenter,
                                                                        )}`}
                                                                    >
                                                                        {formatTelephoneNumberOnly(data?.callCenter)}
                                                                    </StyledLink>
                                                                </StyledTypoInfo>
                                                            </StyledDivText>
                                                        </StyledDivRow>
                                                    )}
                                                    {/* เว็บไซต์ */}
                                                    {data?.website && (
                                                        <StyledDivRow>
                                                            <StyledDivIcon>
                                                                <IconWebsite />
                                                            </StyledDivIcon>
                                                            <StyledDivText>
                                                                <StyledTypoInfo>
                                                                    {`${t('LABEL.BACKOFFICE.CONTACT.WEBSITE')} : `}
                                                                    <StyledLink
                                                                        href={getUrl(data.website)}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {data.website}
                                                                    </StyledLink>
                                                                </StyledTypoInfo>
                                                            </StyledDivText>
                                                        </StyledDivRow>
                                                    )}
                                                    {/* อีเมล */}
                                                    {data?.contactEmail && (
                                                        <StyledDivRow>
                                                            <StyledDivIcon>
                                                                <IconEmail />
                                                            </StyledDivIcon>
                                                            <StyledDivText>
                                                                <StyledTypoInfo>
                                                                    {`${t('LABEL.BACKOFFICE.CONTACT.EMAIL')} : `}
                                                                    <StyledLink href={`mailto:${data?.contactEmail}`}>
                                                                        {data?.contactEmail}
                                                                    </StyledLink>
                                                                </StyledTypoInfo>
                                                            </StyledDivText>
                                                        </StyledDivRow>
                                                    )}
                                                    {/* Line */}
                                                    {data?.lineContact && data?.lineContact?.url && (
                                                        <StyledDivRow>
                                                            <StyledDivIcon>
                                                                <IconLine />
                                                            </StyledDivIcon>
                                                            <StyledDivText>
                                                                <StyledTypoInfo>
                                                                    {`${t('LABEL.BACKOFFICE.CONTACT.LINE')} : `}
                                                                    <StyledLink
                                                                        href={getUrl(data?.lineContact?.url)}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {data?.lineContact?.id}
                                                                    </StyledLink>
                                                                </StyledTypoInfo>
                                                            </StyledDivText>
                                                        </StyledDivRow>
                                                    )}
                                                </StyledDivInfo>
                                            </StyledDivDetail>
                                        )}
                                    </>
                                )}
                            </StyledContent>
                        </StyledContainer>
                    </Scrollbar>
                </div>
            </StyledContent>

            <StyledFooter>
                <CoreFooter />
            </StyledFooter>
        </Dialog>
    )
}

export default InfoContact

const StyledContainer = styled('div')(({ theme }) => ({
    padding: '30px 100px',
    [theme.breakpoints.down('sm')]: {
        padding: '30px 20px',
    },
}))

const StyledFooter = styled('div')(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    width: '100%',
}))

const StyledContent = styled('div')(({ theme }) => ({
    height: `calc(100% - 92px)`,
}))

const StyledDivCover = styled('div')(({ theme }) => ({
    margin: '20px 0px',
}))

const StyledDivCoverNoImage = styled('div')(({ theme }) => ({
    height: '300px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    backgroundColor: `${theme.palette.getContrastText('bg-cover-no-image')}`,
}))

const StyledImgCover = styled('img')(({ theme }) => ({
    width: '100%',
}))

const StyledImgAnnounce = styled('img')(({ theme }) => ({
    width: '70%',
}))

const StyledImgNoImage = styled('img')(({ theme }) => ({
    height: '50%',
}))

const StyledDivNotFound = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '50px',
}))

const StyledDivDetail = styled('div')(({ theme }) => ({
    margin: '20px 0px',
}))

const StyledTypoNBTC = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.secondary.dark,
}))

const StyledDivInfo = styled('div')(({ theme }) => ({
    margin: '20px 0px',
}))

const StyledDivRow = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    margin: '10px 0px',
}))

const StyledDivIcon = styled('div')(({ theme }) => ({
    marginRight: '20px',
}))

const StyledDivText = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
}))

const StyledTypoInfo = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
}))

const StyledLink = styled(Link)(({ theme }) => ({
    color: `${theme.palette.primary.main} !important`,
}))

const IconAddress = styled(AddressIcon)(({ theme }) => ({
    width: '20px',
}))

const IconTelephone = styled(TelephoneIcon)(({ theme }) => ({
    width: '20px',
}))

const IconCallCenter = styled(CallCenterIcon)(({ theme }) => ({
    width: '20px',
}))

const IconWebsite = styled(WebsiteIcon)(({ theme }) => ({
    width: '20px',
}))

const IconEmail = styled(EmailIcon)(({ theme }) => ({
    width: '20px',
}))

const IconLine = styled(LineIcon)(({ theme }) => ({
    width: '20px',
}))
